import { useState } from "react";
import { useTranslation } from "react-i18next";
import { AnimatePresence, motion } from "framer-motion";
import { Check } from "lucide-react";
import { cmsText } from "@/lib/cms";
import { thumbUrl } from "@/lib/product";
import { Cta, Reveal, SectionHeading, SegmentToggle } from "@/components/blocks/shared";

export const BrandStoryBlock = ({ block }) => {
  const { i18n } = useTranslation();
  const lang = i18n.language;
  const T = (f) => cmsText(f, lang);
  const merken = (block.merken || []).filter((m) => m?.naam);
  const [actief, setActief] = useState(0);
  const merk = merken[actief] || merken[0];
  if (!merk) return null;
  const opties = merken.map((m, i) => ({ id: String(i), label: m.naam }));
  const usps = (merk.usps || []).map((u) => T(u.tekst)).filter(Boolean);

  return (
    <section className="relative border-b bg-sand-light" data-testid="block-merk-toggle">
      <div className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8 lg:py-28">
        <Reveal className="flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
          <SectionHeading eyebrow={T(block.eyebrow)} title={T(block.titel)} subtitle={T(block.subtitel)} />
          {opties.length > 1 && <SegmentToggle options={opties} value={String(actief)} onChange={(v) => setActief(Number(v))} testIdPrefix="brand-story" />}
        </Reveal>

        <AnimatePresence mode="wait">
          <motion.div
            key={actief}
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.4, ease: [0.2, 0.8, 0.2, 1] }}
            className="mt-12 grid items-center gap-10 lg:grid-cols-12 lg:gap-14"
            data-testid={`brand-story-panel-${actief}`}
          >
            <div className="relative lg:col-span-6">
              {merk.afbeelding && (
                <div className="relative overflow-hidden rounded-3xl bg-secondary shadow-[0_30px_60px_-30px_hsl(30_20%_20%/0.45)]">
                  <img src={thumbUrl(merk.afbeelding, 1200)} alt={merk.naam} className="aspect-[5/4] w-full object-cover" />
                </div>
              )}
              <div className="absolute -bottom-5 left-6 rounded-2xl bg-ink px-5 py-3 text-ink-foreground shadow-xl">
                <p className="font-mono text-[10px] uppercase tracking-widest opacity-60">{T(block.merk_label)}</p>
                <p className="font-display text-xl font-bold tracking-tight">{merk.naam}</p>
              </div>
            </div>
            <div className="lg:col-span-6">
              {T(merk.tagline) && <p className="font-serif text-2xl italic leading-snug text-primary sm:text-3xl">{T(merk.tagline)}</p>}
              <p className="mt-5 whitespace-pre-line text-base leading-relaxed text-muted-foreground">{T(merk.tekst)}</p>
              {usps.length > 0 && (
                <ul className="mt-7 grid gap-3 sm:grid-cols-2" data-testid="brand-story-usps">
                  {usps.map((u, i) => (
                    <li key={i} className="flex items-start gap-3 rounded-xl border bg-card p-3 text-sm">
                      <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
                        <Check className="h-3 w-3" aria-hidden="true" />
                      </span>
                      {u}
                    </li>
                  ))}
                </ul>
              )}
              <div className="mt-8 flex flex-wrap gap-3">
                <Cta label={T(merk.cta_label)} link={merk.cta_link} testId="brand-story-cta" arrow />
              </div>
            </div>
          </motion.div>
        </AnimatePresence>
      </div>
    </section>
  );
};
